import { useEffect, useRef, useState } from 'react'

type Option = {
  label: string
  value: string
}

export type SearchableSelectProps = {
  label?: string
  options: Option[]
  placeholder?: string
  error?: string
  required?: boolean
  value?: string
  onChange?: (value: string) => void
  disabled?: boolean
}

export function SearchableSelect({
  label,
  options,
  placeholder = 'Type to search...',
  error,
  required = false,
  value,
  onChange,
  disabled
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  const selectedOption = options.find(opt => opt.value === value)
  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  )

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearch('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    setHighlightedIndex(0)
  }, [search])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value)
    if (!isOpen) setIsOpen(true)
  }

  const handleSelect = (opt: Option) => {
    onChange?.(opt.value)
    setIsOpen(false)
    setSearch('')
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true)
        e.preventDefault()
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex(prev => Math.min(prev + 1, filteredOptions.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex(prev => Math.max(prev - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        if (filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex])
        }
        break
      case 'Escape':
        setIsOpen(false)
        setSearch('')
        break
    }
  }

  const handleFocus = () => {
    if (!disabled) {
      setIsOpen(true)
    }
  }

  return (
    <div className="searchable-select" ref={containerRef}>
      {label ? (
        <label className="searchable-select__label">
          {label}
          {required ? <span className="ml-1 text-red-500">*</span> : null}
        </label>
      ) : null}

      <div className="searchable-select__control">
        <input
          type="text"
          className={`searchable-select__input ${error ? 'enrollment-field-control--error' : ''}`}
          placeholder={selectedOption ? selectedOption.label : placeholder}
          value={isOpen ? search : selectedOption?.label || ''}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          aria-required={required}
          disabled={disabled}
          required={required}
        />
        <span
          className={`searchable-select__arrow ${isOpen ? 'searchable-select__arrow--open' : ''}`}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path
              d="M2.5 4.5L6 8L9.5 4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </div>

      {isOpen && (
        <ul className="searchable-select__dropdown">
          {filteredOptions.length === 0 ? (
            <li className="searchable-select__no-results">No results found</li>
          ) : (
            filteredOptions.map((opt, index) => (
              <li
                key={opt.value}
                className={`searchable-select__option ${opt.value === value ? 'searchable-select__option--selected' : ''} ${index === highlightedIndex ? 'searchable-select__option--highlighted' : ''}`}
                onClick={() => handleSelect(opt)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                {opt.label}
              </li>
            ))
          )}
        </ul>
      )}

      {error && <span className="searchable-select__error">{error}</span>}
    </div>
  )
}
